import { Browser } from '@capacitor/browser'
import { IonButton } from '@ionic/react'
import type React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

type MarkdownProps = React.ComponentProps<typeof ReactMarkdown>

// v1 parity: dark toolbar for the in-app browser sheet
const IN_APP_TOOLBAR_COLOR = '#252525'

/**
 * `inApp` in the b64 JSON opens the href inside the Capacitor Browser sheet
 * instead of navigating externally. 'popover' | 'fullscreen' pick the iOS
 * presentation style (`true` falls back to 'fullscreen'); `target` carries
 * over as the web window.open name.
 */
const applyInAppBrowser = (
  inApp: unknown,
  elementProps: Record<string, unknown>,
): Record<string, unknown> => {
  const url = elementProps.href
  if (!inApp || typeof url !== 'string') return elementProps

  const presentationStyle = inApp === 'popover' ? 'popover' : 'fullscreen'
  const windowName =
    typeof elementProps.target === 'string' ? elementProps.target : undefined
  return {
    ...elementProps,
    onClick: (e: React.MouseEvent) => {
      e.preventDefault()
      void Browser.open({
        url,
        presentationStyle,
        windowName,
        toolbarColor: IN_APP_TOOLBAR_COLOR,
      })
    },
  }
}

/**
 * Renders markdown content — parity with the v1 crew app's Markdown component.
 * Enables GitHub-flavored markdown (tables, strikethrough, autolinks, …) and
 * opens links in a new tab. Used for order instructions and crew addendum notes
 * (`crewAddendumMarkdown`), which are authored as markdown.
 *
 * Also decodes v1's `b64#<base64-json>` link encoding: such hrefs carry a JSON
 * blob describing an element + its props, used to embed action buttons (e.g.
 * "Upload Media", "Add / View Photos") inside the markdown content.
 */
export const Markdown = (props: MarkdownProps) => (
  <ReactMarkdown
    {...props}
    remarkPlugins={[...(props.remarkPlugins ?? []), remarkGfm]}
    linkTarget={props.linkTarget || '_blank'}
    components={{
      ...props.components,
      a: ({ href, children, ...rest }) => {
        // b64#<base64 JSON> → an embedded element (IonButton or anchor).
        if (href?.startsWith('b64#')) {
          try {
            const { element, inApp, ...decodedProps } = JSON.parse(
              atob(href.slice('b64#'.length)),
            ) as { element?: string; inApp?: unknown } & Record<string, unknown>
            const elementProps = applyInAppBrowser(inApp, decodedProps)
            const Element: React.ElementType =
              element === 'button' ? IonButton : 'a'
            return <Element {...elementProps}>{children}</Element>
          } catch {
            return null
          }
        }
        return (
          <a href={href} {...rest}>
            {children}
          </a>
        )
      },
    }}
  />
)
